import { analyticsService, techlineService, worksheetsService } from '@web/services/singletons';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import {
    CreateCidCvnDraftCaseParams,
    WorksheetAnswerBody,
    WorksheetAnswerStatus,
    WorksheetElementAnswers,
    WorksheetFormElement,
} from '@packages/models/api';

const logger = getLogger('cid-cvn');

const INPUT_TEXT_TYPE = 'INPUT_TEXT' as const;
const EXPECTED_CID_ELEMENT_LABEL = 'Calibration ID (CID)';
const EXPECTED_CVN_ELEMENT_LABEL = 'Calibration Verification Number (CVN)';

export function useCreateCidCvnCaseWorksheet() {
    return {
        createCaseAndWorksheetSubmission,
    };
}

async function createCaseAndWorksheetSubmission({
    retailerNumber,
    userName,
    vin,
    cid,
    cvn,
    cidCvnStatusId,
    cpoInd,
}: {
    retailerNumber: string;
    userName: string;
    vin: string;
    cid: string;
    cvn: string;
    cidCvnStatusId: string;
    cpoInd: boolean;
}) {
    if (!retailerNumber) {
        throw new Error('We are unable to create a case. The retailer number is not available.');
    }

    let caseNumber: string;
    let worksheetId: string;
    let worksheetFormElements: WorksheetFormElement[];
    let submissionId: string;
    let worksheetAnswerBody: WorksheetAnswerBody;

    try {
        const results = await createDraftCaseAndWorksheet({
            retailerNumber,
            userName,
            vin,
            cid,
            cvn,
            cidCvnStatusId,
            cpoInd,
        });

        caseNumber = results.caseNumber;
        worksheetId = results.worksheetId;
    } catch (error) {
        const errorMessage = error.message || 'We were unable to create a case. Please try again.';
        logger.error(`Failed to create the CID/CVN draft case for VIN [${vin}]`, { error, retailerNumber, vin });
        throw new Error(errorMessage, { cause: error });
    }

    try {
        worksheetFormElements = await fetchWorksheetFormById(worksheetId);
    } catch (error) {
        const errorMessage = 'We were unable to retrieve the worksheet form.';
        logger.error(`Failed to retrieve the worksheet form [${worksheetId}]`, { error, worksheetId });
        throw new Error(errorMessage, { cause: error });
    }

    try {
        worksheetAnswerBody = buildWorksheetAnswerBody(
            caseNumber,
            cid,
            cvn,
            userName,
            worksheetId,
            worksheetFormElements
        );
    } catch (error) {
        const errorMessage = 'We encountered an unexpected error preparing your worksheet.';
        logger.error('Failed to prepare the CID/CVN worksheet answers', { caseNumber, error, worksheetId });
        throw new Error(errorMessage, { cause: error });
    }

    try {
        submissionId = await createWorksheetSubmission(worksheetAnswerBody);
    } catch (error) {
        const errorMessage = 'We were unable to assign your CID and CVN results to the worksheet.';
        logger.error(`Failed to submit the CID/CVN worksheet answers for case [${caseNumber}]`, {
            caseNumber,
            error,
            worksheetId,
        });
        throw new Error(errorMessage, { cause: error });
    }

    return {
        caseNumber,
        worksheetId,
        submissionId,
    };
}

async function createDraftCaseAndWorksheet(
    params: CreateCidCvnDraftCaseParams
): Promise<{ caseNumber: string; worksheetId: string }> {
    try {
        const response = await techlineService.createCidCvnTechlineDraftCase({ body: params });

        if (!response.success) {
            throw new Error(response.data?.message || 'Failed to create draft case and attach worksheet');
        }

        if (!response.data) {
            throw new Error('No data returned from create draft case and attach worksheet');
        }

        analyticsService.logEvent(ANALYTICS_EVENTS.USER_CREATES_DRAFT_TECHLINE_CASE);

        const { caseNumber, cidCvnWorksheetTitles } = response.data;
        analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_CASE_FORM_STARTED, {
            moduleName: 'techline_case',
            caseNumber: caseNumber,
            defaultForm: 'concern',
        });

        return { caseNumber, worksheetId: cidCvnWorksheetTitles.worksheetId };
    } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to create draft case and worksheet';
        logger.error(`Failed to create the CID/CVN draft case and worksheet for VIN [${params.vin}]`, {
            error: e,
            vin: params.vin,
        });
        throw new Error(message);
    }
}

async function fetchWorksheetFormById(worksheetId: string): Promise<WorksheetFormElement[]> {
    try {
        const response = await worksheetsService.fetchWorksheetFormById(worksheetId);
        if (!response.success) {
            throw new Error('Failed to fetch worksheet form by ID');
        }

        if (!response.data) {
            throw new Error('No data returned from fetch worksheet form by ID');
        }

        return response.data.elements;
    } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to fetch worksheet form by ID';
        logger.error(`Failed to fetch the worksheet form [${worksheetId}]`, { error: e, worksheetId });
        throw new Error(message);
    }
}

async function createWorksheetSubmission(worksheetAnswerBody: WorksheetAnswerBody) {
    try {
        const response = await worksheetsService.worksheetAnswerSubmission(worksheetAnswerBody);

        if (!response.success) {
            throw new Error('Failed to create worksheet submission');
        }

        if (!response.data) {
            throw new Error('No data returned from create worksheet submission');
        }
        return response.data.results.submissionId;
    } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to create worksheet submission';
        logger.error(`Failed to create the worksheet submission for case [${worksheetAnswerBody.caseNumber}]`, {
            caseNumber: worksheetAnswerBody.caseNumber,
            error: e,
            worksheetId: worksheetAnswerBody.worksheetId,
        });
        throw e instanceof Error ? e : new Error(message);
    }
}

function buildWorksheetAnswerBody(
    caseNumber: string,
    cid: string,
    cvn: string,
    userName: string,
    worksheetId: string,
    worksheetFormElements: WorksheetFormElement[]
): WorksheetAnswerBody {
    const answersByElementId = createCidCvnAnswers(worksheetFormElements, cid, cvn);

    return {
        answers: Object.values(answersByElementId).map((answer) => ({
            elementId: answer.elementId,
            selectedOptions: answer.selectedOptions,
        })),
        caseNumber,
        status: WorksheetAnswerStatus.Submitted,
        submissionId: '',
        userName,
        worksheetId,
    };
}

function normalizeWorksheetLabel(label: string | null | undefined): string {
    return (label ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function getLabelTokens(label: string | null | undefined): Set<string> {
    const normalizedLabel = normalizeWorksheetLabel(label);
    return new Set(normalizedLabel ? normalizedLabel.split(' ') : []);
}

const normalizedElementLabel = {
    cid: normalizeWorksheetLabel(EXPECTED_CID_ELEMENT_LABEL),
    cvn: normalizeWorksheetLabel(EXPECTED_CVN_ELEMENT_LABEL),
} as const;

function isExactLabel(label: string | null | undefined, expectedNormalized: string): boolean {
    return normalizeWorksheetLabel(label) === expectedNormalized;
}

function isExactCidLabel(label: string | null | undefined): boolean {
    return isExactLabel(label, normalizedElementLabel.cid);
}

function isExactCvnLabel(label: string | null | undefined): boolean {
    return isExactLabel(label, normalizedElementLabel.cvn);
}

function isCidLabel(label: string | null | undefined): boolean {
    const tokens = getLabelTokens(label);
    if (tokens.has('cid')) return true;
    if (tokens.has('calibration') && tokens.has('id') && !tokens.has('verification')) return true;
    if (tokens.has('calibration') && tokens.has('identifier') && !tokens.has('verification')) return true;
    return false;
}

function isCvnLabel(label: string | null | undefined): boolean {
    const tokens = getLabelTokens(label);
    if (tokens.has('cvn')) return true;
    if (tokens.has('calibration') && tokens.has('verification')) return true;
    if (tokens.has('verification') && tokens.has('number') && tokens.has('calibration')) return true;
    return false;
}

function buildAnswerForTextInput(element: WorksheetFormElement, value: string): WorksheetElementAnswers | null {
    const firstOption = element.options?.[0];
    if (!firstOption) return null;

    return {
        elementId: element.worksheetElementId,
        selectedOptions: [
            {
                optionId: firstOption.optionId,
                optionLabel: firstOption.optionLabel,
                answerText: value,
            },
        ],
    };
}

function createCidCvnAnswers(
    elements: WorksheetFormElement[],
    cid: string,
    cvn: string
): Record<string, WorksheetElementAnswers> {
    const answers: Record<string, WorksheetElementAnswers> = {};
    let cidFilled = false;
    let cvnFilled = false;

    const inputTextElements = elements.filter((element) => element.worksheetElementTypeId === INPUT_TEXT_TYPE);

    for (const element of inputTextElements) {
        if (!cvnFilled && isExactCvnLabel(element.worksheetElementLabel)) {
            const answer = buildAnswerForTextInput(element, cvn);
            if (answer) {
                answers[element.worksheetElementId] = answer;
                cvnFilled = true;
            }
        }
        if (!cidFilled && isExactCidLabel(element.worksheetElementLabel)) {
            const answer = buildAnswerForTextInput(element, cid);
            if (answer) {
                answers[element.worksheetElementId] = answer;
                cidFilled = true;
            }
        }
        if (cidFilled && cvnFilled) break;
    }

    if (!cidFilled || !cvnFilled) {
        for (const element of inputTextElements) {
            const label = element.worksheetElementLabel;
            if (!cvnFilled && isCvnLabel(label)) {
                const answer = buildAnswerForTextInput(element, cvn);
                if (answer) {
                    answers[element.worksheetElementId] = answer;
                    cvnFilled = true;
                }
            } else if (!cidFilled && isCidLabel(label)) {
                const answer = buildAnswerForTextInput(element, cid);
                if (answer) {
                    answers[element.worksheetElementId] = answer;
                    cidFilled = true;
                }
            }
            if (cidFilled && cvnFilled) break;
        }
    }

    return answers;
}
